import { useCallback, useMemo, useState } from 'react';

import { DEFAULT_MODE, TRAVEL_MODES, type LatLng, type TravelMode } from './walkshed';

/**
 * Where travel is measured from (contract §3, §11).
 *
 * Home is `cg.home.<city>`: a neighborhood's centroid or a searched address,
 * with the travel mode last used. "Current location" asks the browser once per
 * session and is never stored. Both belong to one city; switching city clears
 * them (`switchCity` removes the old key, and the position is dropped here).
 */

export interface Home {
  label: string;
  lat: number;
  lng: number;
  mode: TravelMode;
  source: 'neighborhood' | 'address';
}

/** Home, the device, or an address searched in the map's popover for this session. */
export type OriginKind = 'home' | 'current' | 'address';

/**
 * A searched place measured from for this session only (contract §17, Session
 * 9): never stored, never in the address bar. It becomes the home only when
 * the user presses Set home.
 */
export interface AddressOrigin {
  label: string;
  point: LatLng;
}

const keyOf = (city: string) => `cg.home.${city}`;
const isCoord = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

function read(city: string): Home | null {
  try {
    const raw = localStorage.getItem(keyOf(city));
    if (!raw) return null;
    const h = JSON.parse(raw) as Partial<Home> | null;
    if (!h || typeof h.label !== 'string' || !isCoord(h.lat) || !isCoord(h.lng)) return null;
    return {
      label: h.label,
      lat: h.lat,
      lng: h.lng,
      mode: TRAVEL_MODES.includes(h.mode as TravelMode) ? (h.mode as TravelMode) : DEFAULT_MODE,
      source: h.source === 'address' ? 'address' : 'neighborhood',
    };
  } catch {
    return null;
  }
}

function write(city: string, home: Home | null): void {
  try {
    if (home) localStorage.setItem(keyOf(city), JSON.stringify(home));
    else localStorage.removeItem(keyOf(city));
  } catch {
    // Site data blocked: home lasts this session.
  }
}

export interface HomeStore {
  home: Home | null;
  /** Null forgets it. */
  setHome: (home: Home | null) => void;
  /** Kept with the home, so it comes back next visit. Nothing to do without one. */
  setMode: (mode: TravelMode) => void;
}

export function useHome(city: string): HomeStore {
  const [state, setState] = useState(() => ({ city, home: read(city) }));
  // Another city: its own home, if it has one.
  let current = state;
  if (state.city !== city) {
    current = { city, home: read(city) };
    setState(current);
  }
  const home = current.home;

  const setHome = useCallback(
    (next: Home | null) => {
      write(city, next);
      setState({ city, home: next });
    },
    [city],
  );
  const setMode = useCallback(
    (mode: TravelMode) =>
      setState((s) => {
        if (!s.home || s.home.mode === mode || s.city !== city) return s;
        const next = { ...s.home, mode };
        write(city, next);
        return { city, home: next };
      }),
    [city],
  );
  return useMemo(() => ({ home, setHome, setMode }), [home, setHome, setMode]);
}

// --- current location ------------------------------------------------------------

export type LocateStatus = 'idle' | 'locating' | 'ready' | 'failed';

/**
 * The position, for this page's life only. Module-level so the map preview and
 * the list share one answer, and the browser is asked once.
 */
let held: { city: string; point: LatLng } | null = null;

export interface CurrentLocation {
  /** The device's position, once it has arrived, for this city. */
  point: LatLng | null;
  status: LocateStatus;
  /** False where the browser has no geolocation at all. */
  supported: boolean;
  /** Asks once; resolves true with a position, false otherwise. */
  request: () => Promise<boolean>;
}

export function useCurrentLocation(city: string): CurrentLocation {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const [state, setState] = useState<{ city: string; point: LatLng | null; status: LocateStatus }>(() => ({
    city,
    point: held?.city === city ? held.point : null,
    status: held?.city === city ? 'ready' : 'idle',
  }));
  let current = state;
  if (state.city !== city) {
    // A position in one city is no origin in another.
    held = null;
    current = { city, point: null, status: 'idle' };
    setState(current);
  }

  const request = useCallback((): Promise<boolean> => {
    if (held?.city === city) {
      const point = held.point;
      setState({ city, point, status: 'ready' });
      return Promise.resolve(true);
    }
    if (!supported) {
      setState({ city, point: null, status: 'failed' });
      return Promise.resolve(false);
    }
    setState({ city, point: null, status: 'locating' });
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const point: LatLng = [pos.coords.latitude, pos.coords.longitude];
          held = { city, point };
          setState({ city, point, status: 'ready' });
          resolve(true);
        },
        () => {
          setState({ city, point: null, status: 'failed' });
          resolve(false);
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60_000 },
      );
    });
  }, [city, supported]);

  return useMemo(
    () => ({ point: current.point, status: current.status, supported, request }),
    [current.point, current.status, supported, request],
  );
}

/**
 * The point the distance filter measures from and what it is: Current or the
 * address when chosen and known, else home, else nothing.
 */
export function resolveOrigin(
  kind: OriginKind,
  home: LatLng | null,
  current: LatLng | null,
  address: AddressOrigin | null = null,
): { point: LatLng; kind: OriginKind } | null {
  if (kind === 'current' && current) return { point: current, kind };
  if (kind === 'address' && address) return { point: address.point, kind };
  return home ? { point: home, kind: 'home' } : null;
}

/** The point alone. */
export function originPoint(
  kind: OriginKind,
  home: LatLng | null,
  current: LatLng | null,
  address: AddressOrigin | null = null,
): LatLng | null {
  return resolveOrigin(kind, home, current, address)?.point ?? null;
}

/** A home's point, as the maths takes it. */
export const homePoint = (home: Home | null): LatLng | null => (home ? [home.lat, home.lng] : null);
